"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { estimatePages } from "@/lib/prompt_template";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";

type Genre = "" | "fiction" | "nonfiction" | "memoir" | "selfhelp";

const genreLabels: Record<Exclude<Genre, "">, string> = {
  fiction: "Fiction (Novel)",
  nonfiction: "Non-fiction (Humanities/Professional)",
  memoir: "Memoir (Personal Narrative)",
  selfhelp: "Self-help (Actionable Guide)",
};

export default function Page() {
  const [genre, setGenre] = useState<Genre>("");

  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("");

  // Fiction
  const [characterFlaw, setCharacterFlaw] = useState("");
  const [genreTrope, setGenreTrope] = useState("");
  const [pov, setPov] = useState<"1st" | "3rd">("3rd");

  // Non-fiction
  const [coreThesis, setCoreThesis] = useState("");
  const [evidenceType, setEvidenceType] = useState("");
  const [targetLevel, setTargetLevel] = useState("");

  // Memoir
  const [lifeChangingEvent, setLifeChangingEvent] = useState("");
  const [emotionalArc, setEmotionalArc] = useState("");
  const [universalTakeaway, setUniversalTakeaway] = useState("");

  // Self-help
  const [painPoint, setPainPoint] = useState("");
  const [fiveStepSolution, setFiveStepSolution] = useState("");
  const [actionableGoal, setActionableGoal] = useState("");

  const [suggestions, setSuggestions] = useState<{
    titles: string[];
    topics: string[];
    audiences: string[];
    characterFlaws: string[];
    genreTropes: string[];
    painPoints: string[];
    fiveStepSolutions: string[];
    actionableGoals: string[];
    coreTheses: string[];
    evidenceTypes: string[];
    targetLevels: string[];
    lifeChangingEvents: string[];
    emotionalArcs: string[];
    universalTakeaways: string[];
  } | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);

  const fetchSuggestions = useCallback(
    async (field?: string) => {
      if (!genre) return;
      setSuggestionsLoading(true);
      setSuggestionsError(null);
    try {
      const res = await fetch("/api/suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          genre,
          title,
          topic,
          audience,
          characterFlaw,
          genreTrope,
          painPoint,
          fiveStepSolution,
          actionableGoal,
          coreThesis,
          evidenceType,
          targetLevel,
          lifeChangingEvent,
          emotionalArc,
          universalTakeaway,
          field,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSuggestionsError(data?.error || "Failed to fetch suggestions.");
        return;
      }
      setSuggestions((prev) => ({
        titles: Array.isArray(data.titles) ? data.titles : prev?.titles ?? [],
        topics: Array.isArray(data.topics) ? data.topics : prev?.topics ?? [],
        audiences: Array.isArray(data.audiences) ? data.audiences : prev?.audiences ?? [],
        characterFlaws: Array.isArray(data.characterFlaws)
          ? data.characterFlaws
          : prev?.characterFlaws ?? [],
        genreTropes: Array.isArray(data.genreTropes)
          ? data.genreTropes
          : prev?.genreTropes ?? [],
        painPoints: Array.isArray(data.painPoints) ? data.painPoints : prev?.painPoints ?? [],
        fiveStepSolutions: Array.isArray(data.fiveStepSolutions)
          ? data.fiveStepSolutions
          : prev?.fiveStepSolutions ?? [],
        actionableGoals: Array.isArray(data.actionableGoals)
          ? data.actionableGoals
          : prev?.actionableGoals ?? [],
        coreTheses: Array.isArray(data.coreTheses) ? data.coreTheses : prev?.coreTheses ?? [],
        evidenceTypes: Array.isArray(data.evidenceTypes)
          ? data.evidenceTypes
          : prev?.evidenceTypes ?? [],
        targetLevels: Array.isArray(data.targetLevels)
          ? data.targetLevels
          : prev?.targetLevels ?? [],
        lifeChangingEvents: Array.isArray(data.lifeChangingEvents)
          ? data.lifeChangingEvents
          : prev?.lifeChangingEvents ?? [],
        emotionalArcs: Array.isArray(data.emotionalArcs)
          ? data.emotionalArcs
          : prev?.emotionalArcs ?? [],
        universalTakeaways: Array.isArray(data.universalTakeaways)
          ? data.universalTakeaways
          : prev?.universalTakeaways ?? [],
      }));
    } catch (error) {
      setSuggestionsError((error as Error)?.message || "Failed to fetch suggestions.");
    } finally {
      setSuggestionsLoading(false);
    }
    },
    [
      genre,
      title,
      topic,
      audience,
      characterFlaw,
      genreTrope,
      painPoint,
      fiveStepSolution,
      actionableGoal,
      coreThesis,
      evidenceType,
      targetLevel,
      lifeChangingEvent,
      emotionalArc,
      universalTakeaway,
    ]
  );

  useEffect(() => {
    setSuggestions(null);
    setSuggestionsError(null);
    if (genre) {
      fetchSuggestions();
    }
  }, [genre, fetchSuggestions]);

  const coreInputsComplete = Boolean(title.trim() && topic.trim() && audience.trim());
  const genreInputsComplete =
    genre === "fiction"
      ? Boolean(characterFlaw.trim() && genreTrope.trim() && pov)
      : genre === "nonfiction"
      ? Boolean(coreThesis.trim() && evidenceType.trim() && targetLevel.trim())
      : genre === "memoir"
      ? Boolean(lifeChangingEvent.trim() && emotionalArc.trim() && universalTakeaway.trim())
      : genre === "selfhelp"
      ? Boolean(painPoint.trim() && fiveStepSolution.trim() && actionableGoal.trim())
      : false;

  useEffect(() => {
    if (genre === "fiction" && coreInputsComplete) {
      fetchSuggestions("Character Flaw");
      fetchSuggestions("Genre Trope");
    }
  }, [genre, coreInputsComplete, fetchSuggestions]);

  useEffect(() => {
    if (genre === "selfhelp" && coreInputsComplete) {
      fetchSuggestions("Pain Point");
      fetchSuggestions("5-Step Solution");
      fetchSuggestions("Actionable Goal");
    }
  }, [genre, coreInputsComplete, fetchSuggestions]);

  useEffect(() => {
    if (genre === "nonfiction" && coreInputsComplete) {
      fetchSuggestions("Core Thesis");
      fetchSuggestions("Evidence Type");
      fetchSuggestions("Target Audience Level");
    }
  }, [genre, coreInputsComplete, fetchSuggestions]);

  useEffect(() => {
    if (genre === "memoir" && coreInputsComplete) {
      fetchSuggestions("Life-Changing Event");
      fetchSuggestions("Emotional Arc");
      fetchSuggestions("Universal Takeaway");
    }
  }, [genre, coreInputsComplete, fetchSuggestions]);

  const payload = useMemo(() => {
    if (!genre) return null;

    const base: Record<string, unknown> = {
      genre,
      common: { title, topic, audience },
    };

    if (genre === "fiction") {
      base.fiction = { characterFlaw, genreTrope, pov };
    } else if (genre === "nonfiction") {
      base.nonfiction = { coreThesis, evidenceType, targetLevel };
    } else if (genre === "memoir") {
      base.memoir = { lifeChangingEvent, emotionalArc, universalTakeaway };
    } else if (genre === "selfhelp") {
      base.selfhelp = { painPoint, fiveStepSolution, actionableGoal };
    }

    return base;
  }, [
    genre,
    title,
    topic,
    audience,
    characterFlaw,
    genreTrope,
    pov,
    coreThesis,
    evidenceType,
    targetLevel,
    lifeChangingEvent,
    emotionalArc,
    universalTakeaway,
    painPoint,
    fiveStepSolution,
    actionableGoal,
  ]);

  const wordCount = useMemo(() => {
    const inputs = [
      title,
      topic,
      audience,
      characterFlaw,
      genreTrope,
      coreThesis,
      evidenceType,
      targetLevel,
      lifeChangingEvent,
      emotionalArc,
      universalTakeaway,
      painPoint,
      fiveStepSolution,
      actionableGoal,
    ];
    return inputs.join(" ").trim().split(/\s+/).filter(Boolean).length;
  }, [
    title,
    topic,
    audience,
    characterFlaw,
    genreTrope,
    coreThesis,
    evidenceType,
    targetLevel,
    lifeChangingEvent,
    emotionalArc,
    universalTakeaway,
    painPoint,
    fiveStepSolution,
    actionableGoal,
  ]);

  const pages = estimatePages(wordCount || 1);

  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [manuscript, setManuscript] = useState<string>("");
  const [chapters, setChapters] = useState<string[]>([]);
  const [milestones, setMilestones] = useState<string[]>([]);
  const [markmapJson, setMarkmapJson] = useState<Record<string, unknown> | null>(null);
  const [generatedWordCount, setGeneratedWordCount] = useState<number | null>(null);
  const [generatedPages, setGeneratedPages] = useState<{ minPages: number; maxPages: number } | null>(
    null
  );
  const [nextLoading, setNextLoading] = useState(false);
  const [nextError, setNextError] = useState<string | null>(null);

  const totalWordCount = useMemo(() => {
    if (!chapters.length) return 0;
    return chapters
      .join(" ")
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
  }, [chapters]);

  const totalPages = useMemo(() => estimatePages(Math.max(totalWordCount, 1)), [totalWordCount]);

  const totalMilestones = milestones.length;
  const currentChapterIndex = chapters.length;
  const isComplete = totalMilestones > 0 && currentChapterIndex >= totalMilestones;

  const handleGenerate = async () => {
    if (!payload) return;
    setGenerateLoading(true);
    setGenerateError(null);
    try {
      const res = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        setGenerateError(data?.error || "Generation failed.");
        return;
      }
      setManuscript(data.manuscript || "");
      setChapters(data.manuscript ? [data.manuscript] : []);
      setMilestones(Array.isArray(data.milestones) ? data.milestones : []);
      setMarkmapJson(data.markmap || null);
      setGeneratedWordCount(typeof data.wordCount === "number" ? data.wordCount : null);
      setGeneratedPages(data.pageEstimate || null);
    } catch (error) {
      setGenerateError((error as Error)?.message || "Generation failed.");
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleNextChapter = async () => {
    if (!payload || !chapters.length || !milestones.length) return;
    setNextLoading(true);
    setNextError(null);
    try {
      const res = await fetch("/api/next-chapter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          milestones,
          chapterIndex: chapters.length + 1,
          lastChapterText: chapters[chapters.length - 1],
          payload,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setNextError(data?.error || "Next chapter generation failed.");
        return;
      }
      if (data.chapterText) {
        setChapters((prev) => [...prev, data.chapterText]);
      }
    } catch (error) {
      setNextError((error as Error)?.message || "Next chapter generation failed.");
    } finally {
      setNextLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0b0c10] text-white">
      <div className="mx-auto max-w-4xl px-6 py-12">
        <header className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a24b]">
            Global AI Author System
          </p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight">
            US Market Manuscript Builder
          </h1>
          <p className="mt-3 text-sm text-slate-300">
            장르를 선택하면 해당 장르에 맞는 핵심 질문이 펼쳐집니다.
          </p>
        </header>

        <section className="rounded-3xl border border-[#2b2f3a] bg-[#141821] p-8">
          <h2 className="text-lg font-semibold">Project Basics</h2>
          <div className="mt-6 grid gap-4">
            <label className="grid gap-2 text-sm">
              Genre
              <select
                className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                value={genre}
                onChange={(e) => setGenre(e.target.value as Genre)}
              >
                <option value="">Select genre...</option>
                {Object.entries(genreLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>

        {genre && (
          <section className="mt-8 rounded-3xl border border-[#2b2f3a] bg-[#0f1218] p-8">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Core Inputs</h2>
            </div>

            {suggestionsError && (
              <p className="mt-2 text-xs text-red-300">{suggestionsError}</p>
            )}

            <div className="mt-6 grid gap-5">
              <label className="grid gap-2 text-sm">
                <span className="flex items-center justify-between">
                  <span>Working Title</span>
                  <button
                    type="button"
                    onClick={() => fetchSuggestions("Working Title")}
                    disabled={suggestionsLoading}
                    className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Refresh
                  </button>
                </span>
                <input
                  className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., The Last Archive"
                />
                <div className="flex flex-wrap gap-2">
                  {suggestionsLoading && !suggestions?.titles?.length ? (
                    <span className="flex items-center gap-2 text-xs text-slate-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading suggestions...
                    </span>
                  ) : suggestions?.titles?.length ? (
                    suggestions.titles.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setTitle(item)}
                        className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                      >
                        <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                        {item}
                      </button>
                    ))
                  ) : (
                    <span className="text-xs text-slate-400">No suggestions yet.</span>
                  )}
                </div>
              </label>

              <label className="grid gap-2 text-sm">
                <span className="flex items-center justify-between">
                  <span>Topic</span>
                  <button
                    type="button"
                    onClick={() => fetchSuggestions("Topic")}
                    disabled={suggestionsLoading}
                    className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Refresh
                  </button>
                </span>
                <input
                  className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., A city built on floating archives"
                />
                <div className="flex flex-wrap gap-2">
                  {suggestionsLoading && !suggestions?.topics?.length ? (
                    <span className="flex items-center gap-2 text-xs text-slate-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading suggestions...
                    </span>
                  ) : suggestions?.topics?.length ? (
                    suggestions.topics.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setTopic(item)}
                        className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                      >
                        <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                        {item}
                      </button>
                    ))
                  ) : (
                    <span className="text-xs text-slate-400">No suggestions yet.</span>
                  )}
                </div>
              </label>

              <label className="grid gap-2 text-sm">
                <span className="flex items-center justify-between">
                  <span>Audience</span>
                  <button
                    type="button"
                    onClick={() => fetchSuggestions("Target Audience")}
                    disabled={suggestionsLoading}
                    className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Refresh
                  </button>
                </span>
                <input
                  className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="e.g., US thriller readers"
                />
                <div className="flex flex-wrap gap-2">
                  {suggestionsLoading && !suggestions?.audiences?.length ? (
                    <span className="flex items-center gap-2 text-xs text-slate-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading suggestions...
                    </span>
                  ) : suggestions?.audiences?.length ? (
                    suggestions.audiences.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setAudience(item)}
                        className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                      >
                        <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                        {item}
                      </button>
                    ))
                  ) : (
                    <span className="text-xs text-slate-400">No suggestions yet.</span>
                  )}
                </div>
              </label>
            </div>
          </section>
        )}

        {genre && coreInputsComplete && (
          <section className="mt-8 rounded-3xl border border-[#2b2f3a] bg-[#0f1218] p-8">
            <h2 className="text-lg font-semibold">Genre-Specific Inputs</h2>

            {genre === "fiction" && (
              <div className="mt-6 grid gap-4">
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>Character Flaw</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("Character Flaw")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={characterFlaw}
                    onChange={(e) => setCharacterFlaw(e.target.value)}
                    placeholder="e.g., Obsessive control"
                  />
                  <div className="flex flex-wrap gap-2">
                  {suggestionsLoading && !suggestions?.characterFlaws?.length ? (
                    <span className="flex items-center gap-2 text-xs text-slate-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading suggestions...
                    </span>
                  ) : suggestions?.characterFlaws?.length ? (
                    suggestions.characterFlaws.map((item) => (
                      <button
                        key={item}
                          type="button"
                          onClick={() => setCharacterFlaw(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                    <span className="text-xs text-slate-400">No suggestions yet.</span>
                  )}
                  </div>
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>Genre Trope</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("Genre Trope")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={genreTrope}
                    onChange={(e) => setGenreTrope(e.target.value)}
                    placeholder="e.g., Hidden heir"
                  />
                  <div className="flex flex-wrap gap-2">
                  {suggestionsLoading && !suggestions?.genreTropes?.length ? (
                    <span className="flex items-center gap-2 text-xs text-slate-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading suggestions...
                    </span>
                  ) : suggestions?.genreTropes?.length ? (
                    suggestions.genreTropes.map((item) => (
                      <button
                        key={item}
                          type="button"
                          onClick={() => setGenreTrope(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                    <span className="text-xs text-slate-400">No suggestions yet.</span>
                  )}
                  </div>
                </label>
                <label className="grid gap-2 text-sm">
                  POV
                  <select
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={pov}
                    onChange={(e) => setPov(e.target.value as "1st" | "3rd")}
                  >
                    <option value="1st">1st</option>
                    <option value="3rd">3rd</option>
                  </select>
                </label>
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>Refresh genre-specific suggestions</span>
                  <button
                    type="button"
                    onClick={() => fetchSuggestions()}
                    disabled={suggestionsLoading}
                    className="flex items-center gap-2 rounded-full border border-[#2b2f3a] px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Refresh
                  </button>
                </div>
              </div>
            )}

            {genre === "nonfiction" && (
              <div className="mt-6 grid gap-4">
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>Core Thesis</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("Core Thesis")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={coreThesis}
                    onChange={(e) => setCoreThesis(e.target.value)}
                    placeholder="e.g., Institutional trust is rebuilt through..."
                  />
                  <div className="flex flex-wrap gap-2">
                    {suggestionsLoading && !suggestions?.coreTheses?.length ? (
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading suggestions...
                      </span>
                    ) : suggestions?.coreTheses?.length ? (
                      suggestions.coreTheses.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setCoreThesis(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No suggestions yet.</span>
                    )}
                  </div>
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>Evidence Type</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("Evidence Type")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={evidenceType}
                    onChange={(e) => setEvidenceType(e.target.value)}
                    placeholder="e.g., Case studies, archival data"
                  />
                  <div className="flex flex-wrap gap-2">
                    {suggestionsLoading && !suggestions?.evidenceTypes?.length ? (
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading suggestions...
                      </span>
                    ) : suggestions?.evidenceTypes?.length ? (
                      suggestions.evidenceTypes.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setEvidenceType(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No suggestions yet.</span>
                    )}
                  </div>
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>Target Audience Level</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("Target Audience Level")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={targetLevel}
                    onChange={(e) => setTargetLevel(e.target.value)}
                    placeholder="e.g., General / Professional / Academic"
                  />
                  <div className="flex flex-wrap gap-2">
                    {suggestionsLoading && !suggestions?.targetLevels?.length ? (
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading suggestions...
                      </span>
                    ) : suggestions?.targetLevels?.length ? (
                      suggestions.targetLevels.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setTargetLevel(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No suggestions yet.</span>
                    )}
                  </div>
                </label>
              </div>
            )}

            {genre === "memoir" && (
              <div className="mt-6 grid gap-4">
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>Life-Changing Event</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("Life-Changing Event")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={lifeChangingEvent}
                    onChange={(e) => setLifeChangingEvent(e.target.value)}
                    placeholder="e.g., Losing a home in a wildfire"
                  />
                  <div className="flex flex-wrap gap-2">
                    {suggestionsLoading && !suggestions?.lifeChangingEvents?.length ? (
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading suggestions...
                      </span>
                    ) : suggestions?.lifeChangingEvents?.length ? (
                      suggestions.lifeChangingEvents.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setLifeChangingEvent(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No suggestions yet.</span>
                    )}
                  </div>
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>Emotional Arc</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("Emotional Arc")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={emotionalArc}
                    onChange={(e) => setEmotionalArc(e.target.value)}
                    placeholder="e.g., Shame → Acceptance → Purpose"
                  />
                  <div className="flex flex-wrap gap-2">
                    {suggestionsLoading && !suggestions?.emotionalArcs?.length ? (
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading suggestions...
                      </span>
                    ) : suggestions?.emotionalArcs?.length ? (
                      suggestions.emotionalArcs.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setEmotionalArc(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No suggestions yet.</span>
                    )}
                  </div>
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>Universal Takeaway</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("Universal Takeaway")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={universalTakeaway}
                    onChange={(e) => setUniversalTakeaway(e.target.value)}
                    placeholder="e.g., Renewal begins with truth"
                  />
                  <div className="flex flex-wrap gap-2">
                    {suggestionsLoading && !suggestions?.universalTakeaways?.length ? (
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading suggestions...
                      </span>
                    ) : suggestions?.universalTakeaways?.length ? (
                      suggestions.universalTakeaways.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setUniversalTakeaway(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No suggestions yet.</span>
                    )}
                  </div>
                </label>
              </div>
            )}

            {genre === "selfhelp" && (
              <div className="mt-6 grid gap-4">
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>Pain Point</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("Pain Point")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={painPoint}
                    onChange={(e) => setPainPoint(e.target.value)}
                    placeholder="e.g., Overwhelm from constant notifications"
                  />
                  <div className="flex flex-wrap gap-2">
                    {suggestionsLoading && !suggestions?.painPoints?.length ? (
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading suggestions...
                      </span>
                    ) : suggestions?.painPoints?.length ? (
                      suggestions.painPoints.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setPainPoint(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No suggestions yet.</span>
                    )}
                  </div>
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>5-Step Solution</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("5-Step Solution")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={fiveStepSolution}
                    onChange={(e) => setFiveStepSolution(e.target.value)}
                    placeholder="e.g., Audit → Prioritize → ..."
                  />
                  <div className="flex flex-wrap gap-2">
                    {suggestionsLoading && !suggestions?.fiveStepSolutions?.length ? (
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading suggestions...
                      </span>
                    ) : suggestions?.fiveStepSolutions?.length ? (
                      suggestions.fiveStepSolutions.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setFiveStepSolution(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No suggestions yet.</span>
                    )}
                  </div>
                </label>
                <label className="grid gap-2 text-sm">
                  <span className="flex items-center justify-between">
                    <span>Actionable Goal</span>
                    <button
                      type="button"
                      onClick={() => fetchSuggestions("Actionable Goal")}
                      disabled={suggestionsLoading}
                      className="flex items-center gap-1 rounded-full border border-[#2b2f3a] px-2 py-1 text-[11px] text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Refresh
                    </button>
                  </span>
                  <input
                    className="rounded-xl border border-[#2b2f3a] bg-black/30 px-3 py-2"
                    value={actionableGoal}
                    onChange={(e) => setActionableGoal(e.target.value)}
                    placeholder="e.g., 30-day focus reset"
                  />
                  <div className="flex flex-wrap gap-2">
                    {suggestionsLoading && !suggestions?.actionableGoals?.length ? (
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading suggestions...
                      </span>
                    ) : suggestions?.actionableGoals?.length ? (
                      suggestions.actionableGoals.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => setActionableGoal(item)}
                          className="flex items-center gap-1 rounded-full border border-[#2b2f3a] bg-black/40 px-3 py-1 text-xs text-slate-200 transition hover:border-[#c9a24b]"
                        >
                          <Sparkles className="h-3 w-3 text-[#c9a24b]" />
                          {item}
                        </button>
                      ))
                    ) : (
                      <span className="text-xs text-slate-400">No suggestions yet.</span>
                    )}
                  </div>
                </label>
              </div>
            )}
          </section>
        )}

        {genre && coreInputsComplete && genreInputsComplete && (
          <section className="mt-8 rounded-3xl border border-[#2b2f3a] bg-[#141821] p-8">
            <h2 className="text-lg font-semibold">Gemini JSON Payload Preview</h2>
            <pre className="mt-4 whitespace-pre-wrap rounded-2xl border border-[#2b2f3a] bg-black/40 p-4 text-xs text-slate-200">
              {payload ? JSON.stringify(payload, null, 2) : "Select a genre to view payload."}
            </pre>
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={!payload || generateLoading}
                className="rounded-2xl bg-[#c9a24b] px-4 py-2 text-sm font-semibold text-black transition hover:brightness-110 disabled:opacity-60"
              >
                {generateLoading ? "Generating..." : "Generate Manuscript"}
              </button>
              {generateError && <span className="text-xs text-red-300">{generateError}</span>}
            </div>
          </section>
        )}

        {genre && coreInputsComplete && genreInputsComplete && (
          <section className="mt-8 rounded-3xl border border-[#2b2f3a] bg-[#0f1218] p-8">
            <h2 className="text-lg font-semibold">6x9 Pagination Estimate</h2>
            <p className="mt-3 text-sm text-slate-300">
              Approx pages for inputs: {pages.minPages}–{pages.maxPages}
            </p>
          </section>
        )}

        {(manuscript || markmapJson) && (
          <section className="mt-8 rounded-3xl border border-[#2b2f3a] bg-[#141821] p-8">
            <h2 className="text-lg font-semibold">Generated Draft</h2>
            {generatedWordCount !== null && generatedPages && (
              <p className="mt-2 text-xs text-slate-300">
                Word count: {generatedWordCount} · Est. pages: {generatedPages.minPages}–
                {generatedPages.maxPages}
              </p>
            )}
            {currentChapterIndex > 0 && totalMilestones > 0 && (
              <p className="mt-2 text-xs text-slate-300">
                Progress: Chapter {currentChapterIndex} of {totalMilestones}
              </p>
            )}
            {currentChapterIndex > 0 && (
              <p className="mt-2 text-xs text-slate-400">
                Total manuscript: {totalWordCount} words · Est. pages: {totalPages.minPages}–
                {totalPages.maxPages}
              </p>
            )}
            {!!milestones.length && (
              <div className="mt-4 rounded-2xl border border-[#2b2f3a] bg-black/40 p-4">
                <h3 className="text-sm font-semibold">Chapter Milestones</h3>
                <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-slate-200">
                  {milestones.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4 grid gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border border-[#2b2f3a] bg-black/40 p-4">
                <h3 className="text-sm font-semibold">Chapters</h3>
                <div className="mt-3 space-y-6 text-xs text-slate-200">
                  {chapters.map((chapter, idx) => (
                    <div key={`${idx}-${chapter.slice(0, 12)}`}>
                      <p className="text-xs font-semibold text-[#c9a24b]">
                        Chapter {idx + 1}
                      </p>
                      <pre className="mt-2 whitespace-pre-wrap">{chapter}</pre>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-[#2b2f3a] bg-black/40 p-4">
                <h3 className="text-sm font-semibold">Markmap JSON</h3>
                <pre className="mt-3 whitespace-pre-wrap text-xs text-slate-200">
                  {markmapJson ? JSON.stringify(markmapJson, null, 2) : "No markmap data."}
                </pre>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={handleNextChapter}
                disabled={nextLoading || isComplete}
                className="rounded-2xl border border-[#2b2f3a] px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-[#c9a24b] disabled:opacity-60"
              >
                {isComplete ? "Manuscript Complete" : nextLoading ? "Generating..." : "Next Chapter"}
              </button>
              {isComplete ? (
                <span className="text-xs text-[#c9a24b]">Manuscript complete.</span>
              ) : nextError ? (
                <span className="text-xs text-red-300">{nextError}</span>
              ) : (
                <span className="text-xs text-slate-400">
                  Continue following milestone order.
                </span>
              )}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
